import { IFormFieldConfig, LocalizedText } from '../models/user-identification';

export type FormFieldOptionRow = { value: string; label: string };

type FormFieldOptionsDeps = {
	translate: (key: string) => unknown;
	resolveLocalizedText: (value?: LocalizedText | null) => string;
	/** Resuelve objetos i18n cuando `translate(key)` devuelve la clave sin traducir. */
	getTranslationTree?: (key: string) => Record<string, string> | null;
};

/**
 * Opciones de selects preset bloqueados en admin (tipo documento, país).
 * El API puede omitir `field.options`; el journey las hidrata desde i18n.
 * Provincia no entra aquí: opciones vía `GET /provinces` (cascada NIF-520).
 */
export function isCatalogBackedFormField(field: Pick<IFormFieldConfig, 'id' | 'name'>): boolean {
	const key = (field.id || field.name).toLowerCase();
	return key === 'id_document_type' || key === 'country';
}

/** Preset provincia del paso dirección (cascada país → provincias). */
export function isProvinceFormField(field: Pick<IFormFieldConfig, 'id' | 'name'>): boolean {
	return (field.id || field.name).toLowerCase() === 'province';
}

/** Preset país (ISO-3); dispara la cascada de provincias. */
export function isCountryFormField(field: Pick<IFormFieldConfig, 'id' | 'name'>): boolean {
	return (field.id || field.name).toLowerCase() === 'country';
}

/** Opciones efectivas del campo: API primero; catálogo i18n como fallback en presets bloqueados. */
export function resolveRawFormFieldOptions(
	field: IFormFieldConfig,
	deps: FormFieldOptionsDeps,
): FormFieldOptionRow[] {
	if (field.options?.length) {
		return field.options.map((option) => ({
			value: option.value,
			label: deps.resolveLocalizedText(option.label),
		}));
	}

	const key = (field.id || field.name).toLowerCase();

	if (key === 'id_document_type') {
		return [
			{ value: 'DNI', label: String(deps.translate('FORM.FIELDS.ID_DOCUMENT_TYPE.DNI')) },
			{ value: 'NIE', label: String(deps.translate('FORM.FIELDS.ID_DOCUMENT_TYPE.NIE')) },
			{ value: 'Pasaporte', label: String(deps.translate('FORM.FIELDS.ID_DOCUMENT_TYPE.PASSPORT')) },
		];
	}

	if (key === 'country') {
		return buildCountryOptions(deps);
	}

	return [];
}

/** Placeholder del `tec-select`: description del campo, clave i18n del preset o genérico. */
export function resolveFormFieldSelectPlaceholder(
	field: IFormFieldConfig,
	deps: FormFieldOptionsDeps,
): string {
	const description = deps.resolveLocalizedText(field.description);
	if (description) {
		return description;
	}

	const placeholderKeys: Partial<Record<string, string>> = {
		id_document_type: 'FORM.FIELDS.ID_DOCUMENT_TYPE.PLACEHOLDER',
		country: 'FORM.FIELDS.COUNTRY.PLACEHOLDER',
		province: 'FORM.FIELDS.PROVINCE.PLACEHOLDER',
	};
	const i18nKey = placeholderKeys[(field.id || field.name).toLowerCase()];
	if (i18nKey) {
		const translated = deps.translate(i18nKey);
		if (typeof translated === 'string' && translated && translated !== i18nKey) {
			return translated;
		}
	}

	const fallback = deps.translate('FORM.SELECT_PLACEHOLDER');
	return typeof fallback === 'string' ? fallback : '';
}

/** Activa búsqueda en `tec-select` para país, provincia o listas largas (>8 opciones). */
export function usesFormFieldSelectSearch(field: IFormFieldConfig, optionCount: number): boolean {
	const key = (field.id || field.name).toLowerCase();
	return key === 'country' || key === 'province' || optionCount > 8;
}

/**
 * Códigos OCR (`DNI_ESP`, `PASSPORT`…) → valores del select FORM (`DNI`, `NIE`, `Pasaporte`).
 * Alineado con `FormOcrFieldMap::toFormDocumentTypeOption` (pwcs_back).
 */
export function toFormDocumentTypeOption(ocrDocumentType: string): string {
	const raw = ocrDocumentType.trim();
	if (!raw) {
		return '';
	}
	if (raw === 'DNI' || raw === 'NIE' || raw === 'Pasaporte') {
		return raw;
	}
	const upper = raw.toUpperCase();
	if (upper === 'DNI' || upper.startsWith('DNI_')) {
		return 'DNI';
	}
	if (upper === 'NIE' || upper === 'TARJ_RESID' || upper.startsWith('NIE')) {
		return 'NIE';
	}
	if (upper === 'PASSPORT' || upper.startsWith('PASSPORT') || raw.toLowerCase() === 'pasaporte') {
		return 'Pasaporte';
	}
	return raw;
}

/**
 * Ajusta `default_value` de selects: normaliza tipo doc OCR y descarta valores fuera de opciones
 * (evita required “fantasma” cuando el select no puede pintar el valor).
 */
export function coerceFormSelectDefaultValue(
	field: Pick<IFormFieldConfig, 'id' | 'name'>,
	rawDefault: unknown,
	optionValues: string[],
): string {
	if (rawDefault === undefined || rawDefault === null) {
		return '';
	}
	let value = String(rawDefault).trim();
	if (value === '') {
		return '';
	}

	const key = (field.id || field.name).toLowerCase();
	if (key === 'id_document_type' || key === 'document_type') {
		value = toFormDocumentTypeOption(value);
	}

	if (optionValues.length > 0 && !optionValues.includes(value)) {
		return '';
	}

	return value;
}

function buildCountryOptions(deps: FormFieldOptionsDeps): FormFieldOptionRow[] {
	const countries = resolveCountryTranslationTree(deps);
	if (!countries) {
		return [];
	}

	return Object.entries(countries)
		.filter(([, label]) => typeof label === 'string' && label.length > 0)
		.map(([value, label]) => ({ value, label: String(label) }))
		.sort((left, right) => left.label.localeCompare(right.label));
}

function resolveCountryTranslationTree(deps: FormFieldOptionsDeps): Record<string, string> | null {
	const instant = deps.translate('COUNTRIES');
	if (instant && typeof instant === 'object' && !Array.isArray(instant)) {
		return instant as Record<string, string>;
	}

	return deps.getTranslationTree?.('COUNTRIES') ?? null;
}
